import { useCallback, useEffect, useState } from 'react';
import { ArrowClockwise, CheckCircle, Package, Pause, X } from 'shared/icons';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import ConfirmModal from '../../ui/ConfirmModal';
import { DateTimeText, Id, Mono, Sku } from '../../ui/Mono';
import { useToast } from '../../ui/Toast';
import { GetProductionOrder, UpdateProductionOrder } from './productionApi';
import { FormatLabel, useProductionLabels } from './productionLabels';
import {
    DescribePutaway,
    ProductionOrderStatusVariant,
    ResolveCurrentProductionLot,
    SplitProductionLocations,
} from './productionModel';

/**
 * FICHA DA OP — abre num MODAL ao clicar na linha (pedido do dono: a coluna
 * lateral apertava a tabela). Duas colunas: à esquerda o que a OP É (produtos,
 * bancada, inventory_id); à direita o que está ACONTECENDO (material, posições,
 * lotes, histórico). As ações ficam embaixo, de largura inteira.
 *
 * A ficha responde, de cima para baixo, as perguntas do Supervisor na ordem em
 * que ele as faz: **em que pé está** (status + o que está na boca da máquina),
 * **por onde ela come e por onde entrega** (a entrada e a saída da máquina onde
 * está aberta), **o que já saiu** (os pallets de saída e os LTs). O botão de
 * liberar/concluir/cancelar fica no fim, porque decidir vem depois de olhar.
 *
 * "Liberar" é o ÚNICO caminho para `RUNNING`: nada no chão inicia uma OP
 * planejada; a bancada só a mostra, esperando a liberação.
 */
const ProductionOrderDetailPanel = ({ orderId, onChanged, onOpenLot }) => {
    const { label } = useProductionLabels();
    const toast = useToast();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [pendingStatus, setPendingStatus] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!orderId) {
            setOrder(null);
            return;
        }
        setLoading(true);
        setLoadError(null);
        try {
            setOrder(await GetProductionOrder(orderId));
        } catch (error) {
            setLoadError(error?.message || 'Erro ao carregar');
            setOrder(null);
        } finally {
            setLoading(false);
        }
    }, [orderId]);

    useEffect(() => {
        load();
    }, [load]);

    const applyStatus = async () => {
        if (!order || !pendingStatus) {
            return;
        }
        setSaving(true);
        try {
            const updated = await UpdateProductionOrder(order.id, { status: pendingStatus });
            setOrder(updated);
            toast.success(label(`wms.production_order_status.${pendingStatus}`));
            // Concluída/cancelada: o pallet atual foi para a logística (ou voltou ao rolo).
            for (const line of DescribePutaway(updated?.putaway, label, FormatLabel)) {
                toast.info(line);
            }
            onChanged?.();
        } catch (error) {
            toast.error(error?.message || 'Erro ao salvar');
        } finally {
            setSaving(false);
            setPendingStatus(null);
        }
    };

    // O supervisor tira a OP da bancada (ex.: o operador saiu sem pausar): ela
    // volta a ficar livre para qualquer bancada abrir.
    const pauseAtBench = async () => {
        if (!order) {
            return;
        }
        setSaving(true);
        try {
            setOrder(await UpdateProductionOrder(order.id, { pause: true }));
            toast.success(label('wms.production.bench_paused'));
            onChanged?.();
        } catch (error) {
            toast.error(error?.message || 'Erro ao salvar');
        } finally {
            setSaving(false);
        }
    };

    if (!orderId) {
        return (
            <aside className="h-full rounded-xl border border-mono-200 bg-white p-6 dark:border-mono-800 dark:bg-mono-950">
                <p className="text-sm text-mono-500 dark:text-mono-400">{label('wms.production.no_selection')}</p>
            </aside>
        );
    }

    const locations = SplitProductionLocations(order);
    const currentLot = ResolveCurrentProductionLot(order);
    const material = order?.current_material ?? null;
    const lots = Array.isArray(order?.production_lots) ? order.production_lots : [];
    const events = Array.isArray(order?.events) ? order.events : [];
    const outputLpns = Array.isArray(order?.output_lpns) ? order.output_lpns : [];

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <Id value={order?.code ?? '…'} className="text-lg font-bold" />
                </div>
                <Button variant="ghost" size="sm" onClick={load} disabled={loading} title={label('wms.bench.refresh')}>
                    <ArrowClockwise size={16} />
                </Button>
            </div>

            {loadError ? <p className="text-sm text-danger-600 dark:text-danger-400">{loadError}</p> : null}

            {order ? (
                <>
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={ProductionOrderStatusVariant(order.status)}>
                            {label(`wms.production_order_status.${order.status}`)}
                        </Badge>
                        {order.machine_code ? <Mono className="text-sm">{order.machine_code}</Mono> : null}
                        {order.kit_size ? (
                            <Badge variant="info">{`${label('wms.field.kit_size')}: ${order.kit_size}`}</Badge>
                        ) : null}
                    </div>

                    <div className="grid gap-6 md:grid-cols-2">
                        <div className="min-w-0 space-y-4">
                            <section className="space-y-1">
                                <p className="text-xs uppercase tracking-wide text-mono-500">{label('wms.field.product_in')}</p>
                                {order.sku_in ? (
                                    <>
                                        <Sku value={order.sku_in} className="text-sm font-medium" />
                                        <p className="text-sm text-mono-600 dark:text-mono-400">{order.product_in_name || '—'}</p>
                                    </>
                                ) : (
                                    <p className="text-sm text-mono-500 dark:text-mono-400">
                                        {label('wms.production.product_in_missing')}
                                    </p>
                                )}
                            </section>

                            <section className="space-y-1">
                                <p className="text-xs uppercase tracking-wide text-mono-500">{label('wms.field.product_out')}</p>
                                <Sku value={order.sku} className="text-sm font-medium" />
                                <p className="text-sm text-mono-600 dark:text-mono-400">{order.product_name || '—'}</p>
                            </section>

                            {/* Onde a OP está AGORA: a OP não tem máquina; a bancada a abre
                                e a pausa a solta. E quanto já saiu, contra a meta. */}
                            <section className="space-y-1 rounded-lg border border-mono-200 p-3 dark:border-mono-800">
                                <p className="text-xs uppercase tracking-wide text-mono-500">{label('wms.production.bench_title')}</p>
                                <p className="text-sm font-medium text-black dark:text-white">
                                    {order.machine_code
                                        ? FormatLabel(label('wms.production.bench_open_at'), { machine: order.machine_code })
                                        : label('wms.production.bench_free')}
                                </p>
                                <p className="text-xs tabular-nums text-mono-600 dark:text-mono-400">
                                    {order.planned_kits
                                        ? FormatLabel(label('wms.production.kits_progress'), {
                                              done: order.kits_done ?? 0,
                                              planned: order.planned_kits,
                                          })
                                        : FormatLabel(label('wms.production.kits_done'), { done: order.kits_done ?? 0 })}
                                </p>
                                {order.machine_code && order.status === 'RUNNING' ? (
                                    <Button size="sm" variant="secondary" onClick={pauseAtBench} disabled={saving}>
                                        <Pause size={14} className="mr-1.5" />
                                        {label('wms.production.bench_pause')}
                                    </Button>
                                ) : null}
                            </section>

                            {/* O inventory_id que a bancada confere — automático, do SKU de
                                saída (anúncios do Full); não é campo da OP. */}
                            <section className="space-y-1">
                                <p className="text-xs uppercase tracking-wide text-mono-500">
                                    {label('wms.production.inventory_id')}
                                </p>
                                {Array.isArray(order.inventory_ids) && order.inventory_ids.length > 0 ? (
                                    <Mono className="text-sm font-medium">{order.inventory_ids.join(' · ')}</Mono>
                                ) : (
                                    <p className="text-sm font-semibold text-danger-600 dark:text-danger-400">
                                        {label('wms.production.inventory_id_missing')}
                                    </p>
                                )}
                            </section>

                            {/* Os PALLETS DE SAÍDA da OP (a bancada bipa um LPN em branco
                                e pode trocar no meio): a OS de transporte de cada um, para
                                onde foi e onde está AGORA. Só leitura. */}
                            <section className="space-y-2 rounded-lg border border-mono-200 p-3 dark:border-mono-800">
                                <p className="text-xs uppercase tracking-wide text-mono-500">
                                    {label('wms.production.output_title')}
                                </p>
                                {outputLpns.length === 0 ? (
                                    <p className="text-sm text-mono-500 dark:text-mono-400">
                                        {label('wms.production.output_none')}
                                    </p>
                                ) : (
                                    <ul className="space-y-2">
                                        {outputLpns.map((lpn) => (
                                            <ProductionOutputLpnRow key={lpn.lpn_code} lpn={lpn} label={label} />
                                        ))}
                                    </ul>
                                )}
                            </section>

                        </div>
                        <div className="min-w-0 space-y-4">
                            {/* O que está NA BOCA da máquina — a primeira pergunta de
                                quem olha uma OP em produção. */}
                            <section className="space-y-1 rounded-lg border border-mono-200 p-3 dark:border-mono-800">
                                <p className="text-xs uppercase tracking-wide text-mono-500">
                                    {label('wms.production.current_material')}
                                </p>
                                {material ? (
                                    <>
                                        <Mono className="block text-base font-bold text-black dark:text-white">
                                            {material.supplier_lot}
                                        </Mono>
                                        <p className="text-xs text-mono-500 dark:text-mono-400">
                                            {label(`wms.machine_material_status.${material.status}`)} ·{' '}
                                            <DateTimeText value={material.started_at} relative />
                                        </p>
                                        {material.lpn_code ? <Mono className="block text-xs">{material.lpn_code}</Mono> : null}
                                    </>
                                ) : (
                                    <p className="text-sm text-mono-500 dark:text-mono-400">
                                        {label('wms.production.no_material')}
                                    </p>
                                )}
                            </section>

                            {/* A entrada e a saída da MÁQUINA onde a OP está aberta (casadas
                                na aba Máquinas). A OP livre não tem posição. */}
                            <section className="space-y-2">
                                <p className="text-xs uppercase tracking-wide text-mono-500">
                                    {label('wms.production.machine_positions')}
                                </p>
                                {locations.all.length === 0 ? (
                                    <p className="text-xs text-mono-500 dark:text-mono-400">
                                        {label('wms.production.machine_positions_free')}
                                    </p>
                                ) : (
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1">
                                            <p className="text-xs font-semibold text-mono-600 dark:text-mono-400">
                                                {label('wms.production.inputs')}
                                            </p>
                                            <Mono className="block text-xs">
                                                {locations.inputs.map((row) => row.location_code).join(', ') || '—'}
                                            </Mono>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs font-semibold text-mono-600 dark:text-mono-400">
                                                {label('wms.production.outputs')}
                                            </p>
                                            <Mono className="block text-xs">
                                                {locations.outputs.map((row) => row.location_code).join(', ') || '—'}
                                            </Mono>
                                        </div>
                                    </div>
                                )}
                            </section>

                            <section className="space-y-2">
                                <p className="text-xs uppercase tracking-wide text-mono-500">
                                    {label('wms.production.lots_of_order')}
                                </p>
                                {lots.length === 0 ? (
                                    <p className="text-sm text-mono-500 dark:text-mono-400">{label('wms.production.no_lots')}</p>
                                ) : (
                                    <ul className="space-y-1">
                                        {lots.map((lot) => (
                                            <li key={lot.id}>
                                                <button
                                                    type="button"
                                                    onClick={() => onOpenLot?.(lot)}
                                                    className="flex w-full items-center justify-between gap-2 rounded px-1 py-1 text-left hover:bg-mono-50 dark:hover:bg-mono-900"
                                                >
                                                    <Mono className="text-xs text-link-600 dark:text-link-400">{lot.code}</Mono>
                                                    <span className="flex items-center gap-2">
                                                        {lot.id === currentLot?.id ? (
                                                            <Badge variant="success">{label('wms.production.current_lot')}</Badge>
                                                        ) : null}
                                                        <span className="text-xs text-mono-500">{lot.production_day}</span>
                                                    </span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </section>

                            {/* O HISTÓRICO: qual máquina abriu, quando pausou (e por quê),
                                quem liberou/finalizou. A OP não guarda máquina; a trilha guarda. */}
                            <section className="space-y-2">
                                <p className="text-xs uppercase tracking-wide text-mono-500">{label('wms.production.history')}</p>
                                {events.length === 0 ? (
                                    <p className="text-sm text-mono-500 dark:text-mono-400">
                                        {label('wms.production.history_empty')}
                                    </p>
                                ) : (
                                    <ol className="space-y-1.5">
                                        {events.map((event) => (
                                            <ProductionOrderEventRow key={event.id} event={event} label={label} />
                                        ))}
                                    </ol>
                                )}
                            </section>

                            {order.notes ? (
                                <section className="space-y-1">
                                    <p className="text-xs uppercase tracking-wide text-mono-500">{label('wms.field.notes')}</p>
                                    <p className="whitespace-pre-wrap text-sm text-mono-600 dark:text-mono-400">{order.notes}</p>
                                </section>
                            ) : null}

                        </div>
                    </div>

                    <section className="flex flex-wrap gap-2 border-t border-mono-200 pt-3 dark:border-mono-800">
                        {order.status === 'PLANNED' ? (
                            <Button size="sm" onClick={() => setPendingStatus('RUNNING')}>
                                <Package size={14} className="mr-1.5" />
                                {label('wms.production.release')}
                            </Button>
                        ) : null}
                        {order.status === 'RUNNING' ? (
                            <Button size="sm" variant="secondary" onClick={() => setPendingStatus('DONE')}>
                                <CheckCircle size={14} className="mr-1.5" />
                                {label('wms.production.finish')}
                            </Button>
                        ) : null}
                        {order.status === 'PLANNED' || order.status === 'RUNNING' ? (
                            <Button size="sm" variant="ghost" onClick={() => setPendingStatus('CANCELED')}>
                                <X size={14} className="mr-1.5" />
                                {label('wms.production.cancel')}
                            </Button>
                        ) : null}
                    </section>
                </>
            ) : null}

            <ConfirmModal
                isOpen={Boolean(pendingStatus)}
                onClose={() => setPendingStatus(null)}
                onConfirm={applyStatus}
                loading={saving}
                title={pendingStatus ? label(`wms.production_order_status.${pendingStatus}`) : ''}
                confirmVariant={pendingStatus === 'CANCELED' ? 'danger' : 'primary'}
                message={
                    pendingStatus === 'CANCELED'
                        ? FormatLabel(label('wms.production.cancel_confirm'), { code: order?.code ?? '' })
                        : pendingStatus === 'DONE'
                          ? FormatLabel(label('wms.production.finish_confirm'), { code: order?.code ?? '' })
                          : FormatLabel(label('wms.production.release_confirm'), { code: order?.code ?? '' })
                }
                confirmText={label('wms.common.save')}
            />

        </div>
    );
};

// Um pallet de saída da OP: o estado, os kits, a OS de transporte, para onde
// foi (o destino que o operador da logística escolheu) e onde está agora.
const OUTPUT_STATE_VARIANT = {
    current: 'success',
    current_forming: 'warning',
    awaiting_pickup: 'info',
    at_factory: 'danger',
    stored: 'default',
    closed: 'default',
};

const ProductionOutputLpnRow = ({ lpn, label }) => (
    <li className="space-y-0.5 rounded border border-mono-100 px-2 py-1.5 text-xs dark:border-mono-800">
        <div className="flex flex-wrap items-center justify-between gap-2">
            <Mono className="text-sm font-medium text-black dark:text-white">{lpn.lpn_code}</Mono>
            <span className="flex items-center gap-1.5">
                <Badge variant="default">{FormatLabel(label('wms.production.output_kits'), { kits: lpn.kits_count ?? 0 })}</Badge>
                <Badge variant={OUTPUT_STATE_VARIANT[lpn.state] ?? 'default'}>
                    {label(`wms.production.output_state_${lpn.state}`)}
                </Badge>
            </span>
        </div>
        {lpn.service_order_code ? (
            <p className="text-mono-600 dark:text-mono-400">
                {label('wms.production.output_os')}: <Mono>{lpn.service_order_code}</Mono>
                {lpn.service_order_status ? ` · ${label(`wms.service_order.status.${lpn.service_order_status}`)}` : ''}
            </p>
        ) : null}
        {lpn.service_order_code ? (
            <p className="text-mono-600 dark:text-mono-400">
                {label('wms.production.output_went_to')}:{' '}
                {lpn.service_order_to_location_code ? (
                    <Mono>{lpn.service_order_to_location_code}</Mono>
                ) : (
                    label('wms.production.output_destination_pending')
                )}
            </p>
        ) : null}
        <p className="text-mono-600 dark:text-mono-400">
            {label('wms.production.output_now_at')}:{' '}
            {lpn.location_code ? <Mono>{lpn.location_code}</Mono> : label('wms.production.output_forming')}
        </p>
    </li>
);

// Uma linha do histórico: o quê (+ por quê), onde, quem e quando.
const ProductionOrderEventRow = ({ event, label }) => {
    const who = event.operator_name
        ? `${event.operator_name}${event.operator_code ? ` (${event.operator_code})` : ''}`
        : event.user_name;
    return (
        <li className="rounded border border-mono-100 px-2 py-1.5 text-xs dark:border-mono-800">
            <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-black dark:text-white">
                    {label(`wms.production_order_event.${event.event_type}`)}
                    {event.machine_code ? (
                        <>
                            {' · '}
                            <Mono>{event.machine_code}</Mono>
                        </>
                    ) : null}
                </span>
                <DateTimeText value={event.created_at} className="shrink-0 text-mono-500" />
            </div>
            {event.reason || who ? (
                <p className="text-mono-500 dark:text-mono-400">
                    {[event.reason ? label(`wms.production_event_reason.${event.reason}`) : null, who]
                        .filter(Boolean)
                        .join(' · ')}
                </p>
            ) : null}
        </li>
    );
};

export default ProductionOrderDetailPanel;
